import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageTitle, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wallet, ReceiptText, Users2, Palette, DollarSign } from "lucide-react";
import { formatRupiah } from "@/lib/midtrans";
import { toast } from "sonner";
import { TEMPLATES } from "@/lib/templates";
import { PREMIUM_THEMES } from "@/lib/premiumThemes";

const PLAN_LABELS = { bulan1: "1 Bulan", bulan6: "6 Bulan", premium: "Premium", none: "Belum" };
const STATUS_COLORS = {
  active: "bg-emerald-500/20 text-emerald-500 border-emerald-500/40",
  pending: "bg-yellow-500/20 text-yellow-500 border-yellow-500/40",
  expired: "bg-red-500/20 text-red-500 border-red-500/40",
  paid: "bg-emerald-500/20 text-emerald-500 border-emerald-500/40",
  failed: "bg-red-500/20 text-red-500 border-red-500/40",
  none: "bg-neutral-500/20 text-neutral-500 border-neutral-500/40",
};

// =========================================================================
export function HargaPaket() {
  const [pricing, setPricing] = useState(null);
  const [popularPlan, setPopularPlan] = useState("bulan6");
  const [savingPopular, setSavingPopular] = useState(false);
  const [saving, setSaving] = useState("");

  const load = async () => {
    const { data } = await api.get("/public/plan-prices");
    setPricing(data.plans);
    setPopularPlan(data.popular_plan || "bulan6");
  };
  useEffect(() => { load(); }, []);

  const update = (plan, patch) => setPricing((p) => ({ ...p, [plan]: { ...p[plan], ...patch } }));

  const savePopular = async (plan) => {
    setSavingPopular(true);
    try {
      await api.put("/admin/popular-plan", { plan });
      setPopularPlan(plan);
      toast.success(`Paket "${PLAN_LABELS[plan]}" ditandai sebagai Populer`);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSavingPopular(false); }
  };

  const save = async (plan) => {
    setSaving(plan);
    const cfg = pricing[plan];
    try {
      await api.put("/admin/plan-prices", {
        plan,
        price: parseInt(cfg.price, 10) || 0,
        original_price: cfg.original_price ? parseInt(cfg.original_price, 10) : 0,
        discount_enabled: !!cfg.discount_enabled,
        discount_type: cfg.discount_type || "time",
        discount_end_at: cfg.discount_end_at || "",
        discount_max_orders: parseInt(cfg.discount_max_orders, 10) || 0,
      });
      toast.success(`${PLAN_LABELS[plan]} disimpan`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(""); }
  };

  if (!pricing) return <div className="p-6 text-sm text-muted-foreground">Memuat...</div>;

  return (
    <div>
      <PageTitle title="Harga Paket & Diskon" desc="Atur harga, harga coret, dan diskon berdasarkan waktu atau kuota order. Perubahan langsung tercermin di landing page & halaman pembayaran." icon={DollarSign} />

      {/* Popular plan selector */}
      <Card className="mb-5 p-5" data-testid="popular-plan-card">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold">Paket Populer di Landing Page</p>
            <p className="text-xs text-muted-foreground">
              Tandai satu paket sebagai "Populer" — akan ditampilkan lebih besar dengan badge menonjol.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "bulan1", label: "1 Bulan" },
              { id: "bulan6", label: "6 Bulan" },
              { id: "premium", label: "Premium" },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                data-testid={`popular-plan-${opt.id}`}
                disabled={savingPopular}
                onClick={() => savePopular(opt.id)}
                className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-all ${
                  popularPlan === opt.id
                    ? "border-primary bg-primary text-primary-foreground shadow"
                    : "border-border hover:border-primary/60"
                }`}
              >
                {popularPlan === opt.id ? "★ " : ""}{opt.label}
              </button>
            ))}
          </div>
        </div>
      </Card>
      <div className="grid gap-5 lg:grid-cols-3">
        {[
          { id: "bulan1", label: "Paket 1 Bulan" },
          { id: "bulan6", label: "Paket 6 Bulan" },
          { id: "premium", label: "Paket Premium" },
        ].map((p) => {
          const cfg = pricing[p.id];
          const disc = !!cfg.discount_enabled;
          return (
            <Card key={p.id} className="p-6" data-testid={`harga-card-${p.id}`}>
              <p className="mb-4 font-heading text-lg">{p.label}</p>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Harga Aktif (yang dibayar)</Label>
                  <Input data-testid={`price-input-${p.id}`} type="number" min="0" value={cfg.price || 0} onChange={(e) => update(p.id, { price: e.target.value })} className="mt-1.5" />
                  <p className="mt-1 text-xs text-muted-foreground">{formatRupiah(parseInt(cfg.price, 10) || 0)}</p>
                </div>
                <div className="flex items-center justify-between rounded-xl border border-border p-3">
                  <div>
                    <Label className="text-xs font-semibold">Aktifkan Diskon (Harga Coret)</Label>
                    <p className="text-[10px] text-muted-foreground">Menampilkan harga coret + harga diskon di landing.</p>
                  </div>
                  <Switch data-testid={`discount-toggle-${p.id}`} checked={disc} onCheckedChange={(v) => update(p.id, { discount_enabled: v })} />
                </div>
                {disc && (
                  <div className="space-y-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">Harga Normal (yang dicoret)</Label>
                      <Input data-testid={`original-price-${p.id}`} type="number" min="0" value={cfg.original_price || 0} onChange={(e) => update(p.id, { original_price: e.target.value })} className="mt-1.5" />
                      <p className="mt-1 text-xs text-muted-foreground">{formatRupiah(parseInt(cfg.original_price, 10) || 0)}</p>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tipe Diskon</Label>
                      <Select value={cfg.discount_type || "time"} onValueChange={(v) => update(p.id, { discount_type: v })}>
                        <SelectTrigger data-testid={`discount-type-${p.id}`} className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="time">Batas Waktu</SelectItem>
                          <SelectItem value="orders">Kuota Order (mis. 100 order pertama)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {cfg.discount_type === "time" ? (
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Berakhir Pada</Label>
                        <Input data-testid={`discount-end-${p.id}`} type="datetime-local" value={cfg.discount_end_at ? cfg.discount_end_at.slice(0, 16) : ""} onChange={(e) => update(p.id, { discount_end_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="mt-1.5" />
                      </div>
                    ) : (
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Kuota Order</Label>
                        <Input data-testid={`discount-max-${p.id}`} type="number" min="1" value={cfg.discount_max_orders || 0} onChange={(e) => update(p.id, { discount_max_orders: e.target.value })} className="mt-1.5" />
                        <p className="mt-1 text-xs text-muted-foreground">Sudah dipakai: <b>{cfg.discount_used_orders || 0}</b> order. Sisa: <b>{Math.max(0, (parseInt(cfg.discount_max_orders, 10) || 0) - (cfg.discount_used_orders || 0))}</b></p>
                      </div>
                    )}
                    <div className="rounded-lg bg-muted/40 p-2 text-xs">
                      Preview: <span className="line-through text-muted-foreground">{formatRupiah(parseInt(cfg.original_price, 10) || 0)}</span>{" "}
                      <b className="text-amber-600">{formatRupiah(parseInt(cfg.price, 10) || 0)}</b>
                    </div>
                  </div>
                )}
                <Button data-testid={`save-price-${p.id}`} onClick={() => save(p.id)} disabled={saving === p.id} className="w-full">
                  {saving === p.id ? "Menyimpan..." : "Simpan"}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// =========================================================================
export function ManajemenPengguna() {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState("");
  const [planFor, setPlanFor] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/users-detailed"); setUsers(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setPlan = async (uid) => {
    const plan = planFor[uid];
    if (!plan) { toast.error("Pilih paket dulu"); return; }
    setBusy(uid);
    try {
      await api.post(`/plans/approve/${uid}`, { plan });
      toast.success(`Paket diaktifkan: ${PLAN_LABELS[plan]}`);
      setPlanFor((p) => ({ ...p, [uid]: "" }));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setBusy(""); }
  };

  return (
    <div>
      <PageTitle title="Manajemen Pengguna" desc="Daftar semua pengguna beserta status paket & pasangan. Ubah paket langsung tanpa proses pembayaran." icon={Users2} />
      <Card className="p-6" data-testid="user-management-card">
        {loading ? <p className="text-sm text-muted-foreground">Memuat...</p> :
          users.length === 0 ? <EmptyState label="Belum ada pengguna." /> :
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Gender</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Paket</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Aktivasi</TableHead>
                  <TableHead>Sisa Waktu</TableHead>
                  <TableHead>Pasangan</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.username}</TableCell>
                    <TableCell>{u.role}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.email}</TableCell>
                    <TableCell>{PLAN_LABELS[u.plan] || u.plan || "-"}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[u.plan_status] || ""}`}>{u.plan_status || "none"}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{(u.plan_activated_at || "").slice(0, 10) || "-"}</TableCell>
                    <TableCell className="text-xs">{u.plan === "premium" && u.plan_status === "active" ? "Selamanya" : (u.plan_days_left != null ? `${u.plan_days_left} hari` : "-")}</TableCell>
                    <TableCell className="text-xs">{u.partner ? u.partner.username : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Select value={planFor[u.id] || ""} onValueChange={(v) => setPlanFor((p) => ({ ...p, [u.id]: v }))}>
                          <SelectTrigger data-testid={`user-plan-select-${u.id}`} className="h-8 w-32 rounded-full text-xs"><SelectValue placeholder="Set paket" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bulan1">1 Bulan</SelectItem>
                            <SelectItem value="bulan6">6 Bulan</SelectItem>
                            <SelectItem value="premium">Premium</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button data-testid={`user-set-plan-${u.id}`} onClick={() => setPlan(u.id)} disabled={busy === u.id || !planFor[u.id]} size="sm" className="rounded-full">Set</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        }
      </Card>
    </div>
  );
}

// =========================================================================
export function RiwayatTransaksi() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/transactions").then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);
  return (
    <div>
      <PageTitle title="Riwayat Transaksi" desc="Daftar semua pembayaran via Midtrans." icon={ReceiptText} />
      <Card className="p-6" data-testid="transactions-card">
        {loading ? <p className="text-sm text-muted-foreground">Memuat...</p> :
          rows.length === 0 ? <EmptyState label="Belum ada transaksi." /> :
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order ID</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Paket</TableHead>
                  <TableHead>Nominal</TableHead>
                  <TableHead>Metode</TableHead>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((t) => (
                  <TableRow key={t.id || t.order_id} data-testid={`tx-row-${t.order_id}`}>
                    <TableCell className="font-mono text-[10px]">{t.order_id}</TableCell>
                    <TableCell>{t.user_username} <span className="text-xs text-muted-foreground">({t.user_email})</span></TableCell>
                    <TableCell>{PLAN_LABELS[t.plan] || t.plan}</TableCell>
                    <TableCell className="font-semibold">{formatRupiah(t.amount)}</TableCell>
                    <TableCell className="text-xs">{t.payment_type || "-"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{(t.created_at || "").replace("T", " ").slice(0, 16)}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[t.status] || ""}`}>{t.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        }
      </Card>
    </div>
  );
}

// =========================================================================
export function ManajemenTemplate() {
  const [disabled, setDisabled] = useState({ classic: [], premium: [] });
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/public/disabled-templates"); setDisabled(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (kind, templateId, enabled) => {
    try {
      await api.put("/admin/templates/toggle", { kind, template_id: templateId, disabled: !enabled });
      await load();
      toast.success(`Template ${enabled ? "diaktifkan" : "dinonaktifkan"}`);
    } catch { toast.error("Gagal"); }
  };

  const isDisabled = (kind, id) => disabled[kind]?.includes(id);

  return (
    <div>
      <PageTitle title="Manajemen Template Undangan" desc="Aktifkan atau nonaktifkan template. Template dinonaktifkan tidak muncul sebagai pilihan pengguna." icon={Palette} />

      <Card className="mb-6 p-6" data-testid="templates-classic-card">
        <p className="mb-3 font-heading text-lg">Template Standar ({TEMPLATES.length})</p>
        {loading ? <p className="text-sm text-muted-foreground">Memuat...</p> :
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {TEMPLATES.map((t) => {
              const enabled = !isDisabled("classic", t.id);
              return (
                <div key={t.id} data-testid={`tpl-classic-${t.id}`} className="flex items-center justify-between rounded-xl border border-border p-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{t.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{t.cat}</p>
                  </div>
                  <Switch data-testid={`tpl-classic-toggle-${t.id}`} checked={enabled} onCheckedChange={(v) => toggle("classic", t.id, v)} />
                </div>
              );
            })}
          </div>
        }
      </Card>

      <Card className="p-6" data-testid="templates-premium-card">
        <p className="mb-3 font-heading text-lg">Template Premium ({PREMIUM_THEMES.length})</p>
        {loading ? <p className="text-sm text-muted-foreground">Memuat...</p> :
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {PREMIUM_THEMES.map((t) => {
              const enabled = !isDisabled("premium", t.id);
              return (
                <div key={t.id} data-testid={`tpl-premium-${t.id}`} className="flex items-center justify-between rounded-xl border border-border p-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="h-10 w-10 flex-shrink-0 rounded-lg" style={{ background: t.palette.bg }}>
                      <span className="flex h-full items-center justify-center text-xs italic" style={{ fontFamily: t.fonts.script, color: t.palette.cream }}>Aa</span>
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{t.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{t.cat}</p>
                    </div>
                  </div>
                  <Switch data-testid={`tpl-premium-toggle-${t.id}`} checked={enabled} onCheckedChange={(v) => toggle("premium", t.id, v)} />
                </div>
              );
            })}
          </div>
        }
      </Card>
    </div>
  );
}
